import React, {ReactElement, createRef, RefObject, ReactNode} from 'react';
import {FilterMatchMode} from 'primereact/api';
import {Column, ColumnFilterElementTemplateOptions} from 'primereact/column';
import {Calendar} from 'primereact/calendar';
import MComponent from '../../Toolkit/Components/Component';
import WithRouterProps from '../../Toolkit/WithRouterProps';
import MPage, {MPageProps} from '../../Toolkit/Components/Shell/Page';
import MTable, {MTableProps} from '../../Toolkit/Components/Controls/Table';
import MToolbar from '../../Toolkit/Components/Controls/Toolbar';
import MButton from '../../Toolkit/Components/Controls/Button';
import WithRouter from '../../Toolkit/WithRouter';
import {withMsal, WithMsalProps} from '@azure/msal-react';
import NSwagAdapter from '../../Toolkit/Api/NSwagAdapter';
import MNSwagClientConfiguration from '../../Toolkit/Api/NSwagClientConfiguration';
import SafeDigAccessTokenProvider from '../../Api/TrafficManagementAccessTokenProvider';
import MMultiDropDown from '../../Toolkit/Components/Controls/MultiDropDown';
import {MDropDownItem} from '../../Toolkit/Components/Controls/DropDownOption';
import {EnumerationClient} from '../../Api/EnumerationClient';
import MessageDialog from '../../Dialogs/MessageDialog';
import {InvoiceTracker, InvoiceTrackerReply} from '../../Api/ControllerTypes/InvoiceTypes';
import {InvoiceClient} from '../../Api/InvoiceClient';
import {utils, writeFile} from 'xlsx';
import MSpacer from '../../Toolkit/Components/Controls/Spacer';
import EnumContext from '../../Context/enum-context';
import {ProjectClient} from '../../Api/ProjectClient';
import {Project} from '../../Api/ControllerTypes/ProjectTypes';

type InvoiceRow = {
    data: InvoiceTracker;
}

type Props = WithMsalProps & WithRouterProps & {};

/** */
type State = {
    page: MPageProps;
    table: MTableProps<InvoiceRow>;
    selectedRow?: InvoiceRow;
    projectList: Project[];
}

class InvoiceTrackerPage extends MComponent<Props, State> {
    static contextType = EnumContext;
    context!: React.ContextType<typeof EnumContext>

    private readonly m_enumerationClient: NSwagAdapter<EnumerationClient>
    private readonly m_invoiceClient: NSwagAdapter<InvoiceClient>;
    private readonly m_projectClient: NSwagAdapter<ProjectClient>;
    private readonly m_pageRef: RefObject<MPage>;
    private readonly m_messageDialogRef: RefObject<MessageDialog>;

    /** */
    public constructor(props: Props) {
        super(props);

        this.bindMethods();

        this.m_invoiceClient = new NSwagAdapter<InvoiceClient>(new InvoiceClient(
            new MNSwagClientConfiguration(process.env.REACT_APP_TRAFFICMANAGEMENT_API_KEY!,
                new SafeDigAccessTokenProvider(this.props.msalContext))));
        this.m_enumerationClient = new NSwagAdapter<EnumerationClient>(new EnumerationClient(
            new MNSwagClientConfiguration(process.env.REACT_APP_TRAFFICMANAGEMENT_API_KEY!,
                new SafeDigAccessTokenProvider(this.props.msalContext))));
        this.m_projectClient = new NSwagAdapter<ProjectClient>(new ProjectClient(
            new MNSwagClientConfiguration(process.env.REACT_APP_TRAFFICMANAGEMENT_API_KEY!,
                new SafeDigAccessTokenProvider(this.props.msalContext))));
        this.m_pageRef = createRef<MPage>();
        this.m_messageDialogRef = createRef<MessageDialog>();

        this.initializeState();

    }

    private bindMethods(): void {

        this.isOpenButtonDisabled = this.isOpenButtonDisabled.bind(this);
        this.isExportDisabled = this.isExportDisabled.bind(this);
        this.requests_Click = this.requests_Click.bind(this);
        this.bookings_Click = this.bookings_Click.bind(this);
        this.export_Click = this.export_Click.bind(this);
        this.crews_Click = this.crews_Click.bind(this);
        this.calendarView_Click = this.calendarView_Click.bind(this);
        this.dayView_Click = this.dayView_Click.bind(this);
        this.invoices_Click = this.invoices_Click.bind(this);
        this.drawings_Click = this.drawings_Click.bind(this);
        this.commercials_Click = this.commercials_Click.bind(this);
        this.table_onSelectionChange = this.table_onSelectionChange.bind(this);
        this.page_onLoadData = this.page_onLoadData.bind(this);
        this.getToolbarTemplate = this.getToolbarTemplate.bind(this);
        this.getCouncilFilterTemplate = this.getCouncilFilterTemplate.bind(this);
        this.getProjectFilterTemplate = this.getProjectFilterTemplate.bind(this);
        this.getJobTypeFilterTemplate = this.getJobTypeFilterTemplate.bind(this);
        this.getInvoicedFilterTemplate = this.getInvoicedFilterTemplate.bind(this);
    }

    /** */
    private initializeState(): void {

        //  Page
        const page: MPageProps = {
            locked: false
        };

        //  Table
        const table: MTableProps<InvoiceRow> = {
            rowData: [],
            filters: {
                'data.invoice.modifiedOn': {
                    value: null,
                    matchMode: FilterMatchMode.DATE_IS
                },
                'data.request.estimate': {
                    value: null,
                    matchMode: FilterMatchMode.CONTAINS
                },
                'data.request.jobType': {
                    value: null,
                    matchMode: FilterMatchMode.IN
                },
                'data.invoice.modifiedBy': {
                    value: null,
                    matchMode: FilterMatchMode.CONTAINS
                },
                'data.invoice.poNumber': {
                    value: null,
                    matchMode: FilterMatchMode.CONTAINS
                },
                'data.invoice.councilId': {
                    value: null,
                    matchMode: FilterMatchMode.IN
                },
                'data.invoice.invoiceNumber': {
                    value: null,
                    matchMode: FilterMatchMode.CONTAINS
                },
                'data.invoice.invoiced': {
                    value: null,
                    matchMode: FilterMatchMode.IN
                }
            }
        };

        this.state = {
            page: page,
            table: table,
            selectedRow: undefined,
            projectList: []
        };

    }

    private isOpenButtonDisabled(): boolean {
        return this.state.selectedRow === undefined;
    }

    private isExportDisabled(): boolean {
        const allowedRoles: string[] = ['Booking', 'Supervisor'];
        const roles = this.props.msalContext.accounts[0].idTokenClaims!.roles;
        const access = allowedRoles.some(a => roles?.includes(a));

        return !access;
    }

    private bookings_Click(): void {
        this.props.navigate(`/bookings`);
    }

    private export_Click(): void {
        const currencyFormat = new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'GBP'
        });

        const data = this.state.table.rowData.map(row => {
            const projectIndex = this.state.projectList.findIndex(value => value.id === row.data.request.projectValue);
            const jobTypeIndex = -1 //this.context?.enums!.jobTypes.findIndex(value => value.id === row.data.request.jobType);
            const councilIndex = this.context?.enums!.councilList.findIndex(value => value.id === row.data.invoice.councilId);
            return ({
            date: row.data.invoice.modifiedOn,
            project: projectIndex !== -1 ? this.state.projectList[projectIndex].code : '',
            estimate: row.data.request.estimate,
            applicationType: jobTypeIndex !== -1 ? this.context?.enums!.jobTypes[jobTypeIndex].name : '',
            applicant: row.data.invoice.modifiedBy,
            poNumber: row.data.invoice.poNumber,
            location: row.data.request.address1 + ' '
                + row.data.request.address2 + ' '
                + row.data.request.address3 + ' '
                + row.data.request.city + ' '
                + row.data.request.county,
            workdates: row.data.invoice.workStart!.toLocaleDateString() + ' - ' + row.data.invoice.workStart!.toLocaleDateString(),
            council: councilIndex !== -1 ? this.context?.enums!.councilList[councilIndex].name : '',
            charge: currencyFormat.format(row.data.invoice.charge),
            invoiceNumber: row.data.invoice.invoiceNumber,
            invoiced: row.data.invoice.invoiced ? 'Yes' : 'No',
            notes: row.data.invoice.notes
        })});

        const workbook = utils.book_new();
        const worksheet = utils.json_to_sheet(data);
        utils.book_append_sheet(workbook, worksheet, "Purchase Orders");

        //Update column headers
        utils.sheet_add_aoa(worksheet, [['Date', 'Project', 'Estimate', 'Application Type', 'Applicant',
                                         'PO Number', 'Location', 'Work Dates', 'Council', 'Charge',
                                         'PO Number', 'Invoiced', 'Notes']], {origin: 'A1'});

        writeFile(workbook, "PurchaseOrders.xlsx", {compression: true});
    }

    private requests_Click(): void {
        this.props.navigate(`/requests`);
    }

    private crews_Click(): void {
        this.props.navigate('/crews');
    }

    private calendarView_Click(): void {
        this.props.navigate('/calendarview');
    }

    private dayView_Click(): void {
        this.props.navigate('/dayview');
    }

    private invoices_Click(): void {
        this.props.navigate('/invoices');
    }

    private drawings_Click(): void {
        this.props.navigate('/drawings');
    }

    private commercials_Click(): void {
        this.props.navigate('/commercials');
    }

    private async table_onSelectionChange(selection: InvoiceRow | InvoiceRow[] | undefined): Promise<void> {

        this.safeSetState((draft: State) =>
            draft.selectedRow = selection as InvoiceRow | undefined);

    }

    private async page_onLoadData(): Promise<void> {
        //Get Row Data
        const listResult = await this.m_invoiceClient.callWithReply(
            InvoiceTrackerReply, (client) => client.getTracker());
        listResult.throwIfFailed();

        this.safeSetState((draft: State) => {

            draft.table.rowData = listResult.reply!.invoices.map((w: InvoiceTracker) => {
                return {data: w} as InvoiceRow;
            });

        });
    }

    private getToolbarTemplate(): ReactElement<MToolbar> {

        return (
            <MToolbar>
                <MButton label="CAD Requests"
                         color="info"
                         icon={"pi pi-question"}
                         iconOnly={false}
                         disabled={false}
                         onClick={this.requests_Click}/>
                <MSpacer/>
                <MButton label="Bookings"
                         color={"info"}
                         icon={"pi pi-book"}
                         iconOnly={false}
                         disabled={false}
                         onClick={this.bookings_Click}/>
                <React.Fragment>
                    <MSpacer/>
                    <MButton label="TM Crews"
                             color="info"
                             icon={"pi pi-users"}
                             iconOnly={false}
                             disabled={false}
                             onClick={this.crews_Click}/>
                    <MSpacer/>
                    <MButton label="Calendar View"
                             color="info"
                             icon={"pi pi-calendar"}
                             iconOnly={false}
                             disabled={false}
                             onClick={this.calendarView_Click}/>
                    <MSpacer/>
                    <MButton label="Day View"
                             color="info"
                             icon={"pi pi-calendar"}
                             iconOnly={false}
                             disabled={false}
                             onClick={this.dayView_Click}/>
                    <MSpacer/>
                    <MButton label="Drawings"
                             color="info"
                             icon={"pi pi-images"}
                             disabled={false}
                             onClick={this.drawings_Click}/>
                    <MSpacer/>
                    <MButton label="Commercials"
                             color="info"
                             icon={"pi pi-wallet"}
                             disabled={false}
                             onClick={this.commercials_Click}/>
                </React.Fragment>
                <MSpacer/>
                <MButton label="Export"
                         color={"success"}
                         icon={"pi pi-file-excel"}
                         iconOnly={false}
                         disabled={this.isExportDisabled}
                         onClick={this.export_Click}/>
            </MToolbar>
        );

    }

    private dateFilterTemplate = (options: any) => {
        return <Calendar value={options.value}
                         onChange={(e: any) => options.filterApplyCallback(e.value, options.index)}
                         dateFormat="dd/mm/yy"
                         placeholder="dd/mm/yyyy"
                         mask="99/99/9999"/>;
    };

    private getProjectFilterTemplate(options: ColumnFilterElementTemplateOptions): ReactNode {
        if(this.context.enums === null) {
            return <div></div>;
        }

        const itemList : MDropDownItem<number>[] = (
            this.state.projectList
                .map((enumvalue) => {
                    return {label: enumvalue.code + ' - ' + enumvalue.name, value: enumvalue.id};
                })
        );

        return (
            <MMultiDropDown id="projectFilter"
                            items={itemList}
                            disabled={false}
                            onChange={(selection: number[]) => options.filterApplyCallback(selection)}/>
        );
    }

    private getJobTypeFilterTemplate(options: ColumnFilterElementTemplateOptions): ReactNode {
        if(this.context.enums === null) {
            return <div></div>;
        }

        const itemList : MDropDownItem<number>[] = (
            this.context?.enums!.jobTypes
                .map((enumvalue) => {
                    return {label: enumvalue.name, value: enumvalue.id};
                })
        );

        return (
            <MMultiDropDown id="jobTypeFilter"
                            items={itemList}
                            disabled={false}
                            onChange={(selection: number[]) => options.filterApplyCallback(selection)}/>
        );
    }

    private getCouncilFilterTemplate(options: ColumnFilterElementTemplateOptions): ReactNode {
        if(this.context.enums === null) {
            return <div></div>;
        }

        const itemList : MDropDownItem<number>[] = (
            this.context?.enums!.councilList
                .map((enumvalue) => {
                    return {label: enumvalue.name, value: enumvalue.id};
                })
        );

        return (
            <MMultiDropDown id="councilFilter"
                            items={itemList}
                            disabled={false}
                            onChange={(selection: number[]) => options.filterApplyCallback(selection)}/>
        );
    }

    private getInvoicedFilterTemplate(options: ColumnFilterElementTemplateOptions): ReactNode {
        const itemList: MDropDownItem<boolean>[] = [
            {label: 'Yes', value: true},
            {label: 'No', value: false}
        ];

        return (
            <MMultiDropDown id="invoicedFilter"
                            items={itemList}
                            disabled={false}
                            onChange={(selection: boolean[]) => options.filterApplyCallback(selection)}/>
        );
    }

    private dateTemplate = (rowData: InvoiceRow) => {
        if(rowData.data.invoice.modifiedOn != null) {
            try {
                return rowData.data.invoice.modifiedOn!.toLocaleDateString();
            } catch {
                return '';
            }
        }
        return '';
    };

    private workDateTemplate = (rowData: InvoiceRow) => {
        let returnString = '';

        if(rowData.data.invoice.workStart != null) {
            try {
                returnString = rowData.data.invoice.workStart!.toLocaleDateString();
            } catch {
            }
        }

        if(rowData.data.invoice.workEnd != null) {
            try {
                returnString += '-' + rowData.data.invoice.workEnd!.toLocaleDateString();
            } catch {
            }
        }

        return returnString;
    };

    private projectTemplate = (rowData: InvoiceRow) => {
        const index = this.state.projectList.findIndex(value => value.id === rowData.data.request.projectValue);

        if(index === undefined || index === -1) {
            return null;
        }

        return <span>
            <div>{this.state.projectList[index!].code}</div>
        </span>;
    };

    private jobTypeTemplate = (rowData: InvoiceRow) => {
        const returnData = rowData.data.request.jobType.map((jt) => {
                const index = this.context.enums?.jobTypes.findIndex(value => value.id === jt);
                return index === undefined && index === -1
                       ? ''
                       : this.context.enums?.jobTypes[index!].name ?? '';
            }
        )

        return <span>
            <div>{returnData.join(', ')}</div>
        </span>;
    };

    private locationTemplate = (rowData: InvoiceRow) => {
        return <span style={{fontSize: 'x-small'}}>
            {rowData.data.request.address1 + ' '
                + rowData.data.request.address2 + ' '
                + rowData.data.request.address3 + ' '
                + rowData.data.request.city + ' '
                + rowData.data.request.county}
        </span>
    };

    private councilTemplate = (rowData: InvoiceRow) => {
        const index = this.context.enums?.councilList.findIndex(value => value.id === rowData.data.invoice.councilId);

        if(index === undefined || index === -1) {
            return null;
        }

        return this.context.enums?.councilList[index!].name;
    };

    public render(): ReactElement {
        const currencyFormat = new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'GBP'
        });

        return (
            <MPage title="Purchase Orders"
                   locked={this.state.page.locked}
                   toolbar={this.getToolbarTemplate()}
                   onLoadData={this.page_onLoadData}
                   ref={this.m_pageRef}>

                <MessageDialog ref={this.m_messageDialogRef}/>

                <MTable
                    rowKey="data.invoice.id"
                    rowData={this.state.table.rowData}
                    filters={this.state.table.filters}
                    onSelectionChange={this.table_onSelectionChange}
                    sortMode={'multiple'}
                    multiSortMeta={[{field: 'data.invoice.id', order: 1}]}>

                    <Column field="data.invoice.modifiedOn"
                            header="Date"
                            dataType='date'
                            body={this.dateTemplate}
                            filter={true}
                            filterElement={this.dateFilterTemplate}
                            sortable={true}
                            style={{width: '8%'}}/>
                    <Column field="data.request.projectValue"
                            header="Project"
                            body={this.projectTemplate}
                            filter={false}
                            sortable={false}
                            style={{width: '8%'}}/>
                    <Column field="data.request.estimate"
                            header="Estimate"
                            filter={true}
                            sortable={true}
                            style={{width: '8%'}}/>
                    <Column field="data.request.jobType"
                            header="Application Type"
                            body={this.jobTypeTemplate}
                            filter={true}
                            filterElement={this.getJobTypeFilterTemplate}
                            showFilterMenu={false}
                            showClearButton={false}
                            sortable={true}
                            style={{width: '12%'}}/>
                    <Column field="data.invoice.modifiedBy"
                            header="Applicant"
                            filter={true}
                            sortable={true}
                            style={{width: '10%'}}/>
                    <Column field="data.invoice.poNumber"
                            header="PO Number"
                            filter={true}
                            sortable={true}
                            style={{width: '10%'}}/>
                    <Column field="data.request.address1"
                            header="Location"
                            body={this.locationTemplate}
                            filter={false}
                            sortable={true}
                            style={{width: '10%'}}/>
                    <Column field="data.invoice.workStart"
                            header="Work Dates"
                            body={this.workDateTemplate}
                            filter={false}
                            sortable={true}
                            style={{width: '10%'}}/>
                    <Column field="data.invoice.councilId"
                            header="Council"
                            body={this.councilTemplate}
                            filter={true}
                            filterElement={this.getCouncilFilterTemplate}
                            showFilterMenu={false}
                            showClearButton={false}
                            sortable={true}
                            style={{width: '10%'}}/>
                    <Column field="data.invoice.charge"
                            header="Charge"
                            body={(rowData: InvoiceRow) => currencyFormat.format(rowData.data.invoice.charge)}
                            filter={false}
                            sortable={true}
                            style={{width: '10%'}}/>
                    <Column field="data.invoice.invoiceNumber"
                            header="PO Number"
                            filter={true}
                            sortable={true}
                            style={{width: '10%'}}/>
                    <Column field="data.invoice.invoiced"
                            header="Invoiced"
                            body={(rowData: InvoiceRow) => rowData.data.invoice.invoiced ? 'Yes' : 'No'}
                            filter={true}
                            filterElement={this.getInvoicedFilterTemplate}
                            showFilterMenu={false}
                            showClearButton={false}
                            sortable={true}
                            style={{width: '10%'}}/>
                    <Column field="data.invoice.notes"
                            header="Comments"
                            filter={false}
                            sortable={false}
                            style={{width: '10%'}}/>
                </MTable>
            </MPage>
        );
    }
}

export default withMsal(WithRouter(InvoiceTrackerPage));
